// Autosave in bursts, shared by Org settings and the work item's description.
// Every change saves itself; changes made within a pause go as one write, one
// write at a time, in the order made. Moved from OrgSettingsScreen.tsx as is.

/** The record's current values for the keys a change is about to touch. */
export const pick = <T extends object>(record: T, like: Partial<T>): Partial<T> =>
  Object.fromEntries(Object.keys(like).map((k) => [k, record[k as keyof T]])) as Partial<T>;

// One write per burst. A stepper's ± was one whole-policy PATCH per click, and
// a failure in the middle of a run rolled the screen back to a stale step while
// later writes had already landed (ORG-12). Changes to one record made within
// the pause are sent as one write, together with the values from before the
// FIRST of them: one thing to save, one to roll back, one to undo. A run that
// ends where it began sends nothing.
// ponytail: closing the browser tab inside the pause loses that last change
// (the screen flushes when it unmounts, not on pagehide). Send with `keepalive`
// on pagehide if that ever costs someone a setting.
//
// A write's `before` is a snapshot of whole columns, and `policy` is one jsonb
// column holding every policy on this screen. The snapshot is only right while
// its write is the last change to those columns. Undo on an older receipt used
// to put it back regardless, silently reverting what had been saved since
// (archive two event types, undo the first, and both came back), and a failed
// write's rollback wiped newer edits the same way. So each write is told which
// of its keys have been changed again (`Turn`), and writes go one at a time, in
// the order made, so a change made on top of one that failed can be restarted
// from what is actually stored.
//
// An Undo is never one write with the changes around it. It took their note:
// set Week start, Undo the lunch break inside the pause, and the receipt read
// "Week start and 1 more put back." with no Undo for a week start just set.
export const SAVE_PAUSE_MS = 600;
export type Burst<D extends object, N> = {
  /** `was`: the record's values for these keys now, before the change. Resolves
   *  when the write that carries the change settles: true if it was saved. */
  add: (change: Partial<D>, was: Partial<D>, note?: N) => Promise<boolean>;
  flush: () => void;
};
export type Turn<D> = {
  /** True while none of this write's keys has been changed again. */
  latest: () => boolean;
  /** `values`, less the keys that have been changed again since this write. */
  untouched: (values: Partial<D>) => Partial<D>;
};
export const createBurst = <D extends object, N extends { undo?: boolean } | undefined = undefined>(
  send: (
    delta: Partial<D>,
    before: Partial<D>,
    note: N | undefined,
    turn: Turn<D>,
  ) => Promise<boolean>,
  ms = SAVE_PAUSE_MS,
): Burst<D, N> => {
  type Batch = {
    delta: Partial<D>;
    before: Partial<D>;
    note?: N;
    waiting: ((ok: boolean) => void)[];
  };
  // Changes not yet sent, oldest first. The last takes more until it is flushed.
  const unsent: Batch[] = [];
  let open = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  // How many times each key has been changed.
  const turns = new Map<string, number>();
  let queue: Promise<void> = Promise.resolve();
  const run = async () => {
    const { delta: d, before: b, note: n, waiting: w } = unsent.shift()!;
    for (const k of Object.keys(d) as (keyof D)[])
      if (JSON.stringify(d[k]) === JSON.stringify(b[k])) delete d[k];
    const done = (ok: boolean) => w.forEach((resolve) => resolve(ok));
    if (!Object.keys(d).length) return done(true);
    const was = pick(b as D, d);
    const at = new Map(turns);
    const fresh = (k: string) => turns.get(k) === at.get(k);
    const turn: Turn<D> = {
      latest: () => Object.keys(d).every(fresh),
      untouched: (values) =>
        Object.fromEntries(Object.entries(values).filter(([k]) => fresh(k))) as Partial<D>,
    };
    const ok = await send(d, was, n, turn).catch(() => false);
    // A change made while this one was on its way was made on top of it. If this
    // one did not land, the newer one starts from what is stored, so its own
    // rollback, Undo and receipt tell the truth.
    if (!ok)
      for (const k of Object.keys(d) as (keyof D)[]) {
        const next = unsent.find((u) => k in u.before);
        if (next) next.before[k] = was[k];
      }
    done(ok);
  };
  const flush = () => {
    clearTimeout(timer);
    if (!open) return;
    open = false;
    queue = queue.then(run);
  };
  return {
    add(change, was, n) {
      if (open && !!unsent[unsent.length - 1].note?.undo !== !!n?.undo) flush();
      if (!open) unsent.push({ delta: {}, before: {}, waiting: [] });
      open = true;
      const batch = unsent[unsent.length - 1];
      for (const k of Object.keys(change)) turns.set(k, (turns.get(k) ?? 0) + 1);
      batch.before = { ...was, ...batch.before };
      batch.delta = { ...batch.delta, ...change };
      batch.note = n ?? batch.note;
      clearTimeout(timer);
      timer = setTimeout(flush, ms);
      return new Promise((resolve) => batch.waiting.push(resolve));
    },
    flush,
  };
};
